import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import {
  IKnmErknmFormViolation,
  IKnmErknmFormViolationEvent,
  IKnmErknmFormViolationEventMaterial,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IKnmErknmViolationEvidence } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmViolationEvidence';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

type FormApi = ICommonFormRef['current'];

interface IActCleanupTarget {
  actPath: string;
  requirements: IKnmErknmFormViolatedRequirementsNpa[] | undefined;
  evidences: IKnmErknmViolationEvidence[] | undefined;
}

/** Акт(ы) формы: один для ИР/ПМ, все acts для КНМ */
const getActCleanupTargets = (
  form: FormApi,
  isKnm: boolean,
  isPm: boolean,
  isDecision: boolean,
): IActCleanupTarget[] => {
  if (isDecision) {
    const actPath = 'organization.act';

    return [
      {
        actPath,
        evidences: form.getFieldState(`${actPath}.violationEvidences`)?.value as
          | IKnmErknmViolationEvidence[]
          | undefined,
        requirements: form.getFieldState(`${actPath}.violatedMandatoryRequirements`)?.value as
          | IKnmErknmFormViolatedRequirementsNpa[]
          | undefined,
      },
    ];
  }

  if (isPm) {
    const actPath = 'act';

    return [
      {
        actPath,
        requirements: form.getFieldState(`${actPath}.violatedMandatoryRequirements`)?.value as
          | IKnmErknmFormViolatedRequirementsNpa[]
          | undefined,
        evidences: undefined,
      },
    ];
  }

  if (isKnm) {
    const acts = form.getFieldState('acts')?.value as
      | Array<{
          violatedMandatoryRequirements?: IKnmErknmFormViolatedRequirementsNpa[];
        }>
      | undefined;

    return (acts || []).map((act, index) => ({
      actPath: `acts[${index}]`,
      requirements: act?.violatedMandatoryRequirements,
      evidences: undefined,
    }));
  }

  return [];
};

const removeLinkedVideoFixationsFromRequirements = (
  form: FormApi,
  requirementsPath: string,
  requirements: IKnmErknmFormViolatedRequirementsNpa[] | undefined,
  videoFixationGuids: Array<string | null | undefined>,
): void => {
  const guidsToRemove = new Set(videoFixationGuids.filter((guid): guid is string => !!guid));

  if (!guidsToRemove.size || !requirements?.length) {
    return;
  }

  requirements.forEach((npa, npaIndex) => {
    npa.violatedMandatory?.forEach((mandatory, mandatoryIndex) => {
      mandatory.violations?.forEach((violation, violationIndex) => {
        violation.events?.forEach((event, eventIndex) => {
          const materials = event.materials as IKnmErknmFormViolationEventMaterial[] | null | undefined;

          if (!materials?.length) {
            return;
          }

          const nextMaterials = materials
            .map(material => {
              if (!material.videoFixations?.length) {
                return material;
              }

              const filteredVideoFixations = material.videoFixations.filter(
                item => !guidsToRemove.has(item.videoFixationOption?.value as string),
              );

              if (filteredVideoFixations.length === material.videoFixations.length) {
                return material;
              }

              return {
                ...material,
                videoFixations: filteredVideoFixations.length ? filteredVideoFixations : null,
              };
            })
            .filter(
              material =>
                !!material.videoFixations?.length ||
                !!material.typeDocConfirmationViolation ||
                !!material.attachments?.length ||
                !!material.links?.length,
            );

          const hasChanges =
            nextMaterials.length !== materials.length ||
            nextMaterials.some((material, index) => material !== materials[index]);

          if (!hasChanges) {
            return;
          }

          form.change(
            `${requirementsPath}[${npaIndex}].violatedMandatory[${mandatoryIndex}].violations[${violationIndex}].events[${eventIndex}].materials`,
            nextMaterials.length ? nextMaterials : null,
          );
        });
      });
    });
  });
};

/**
 * Удаляет videoFixations, ссылающиеся на удалённые подтверждающие материалы акта,
 * во всех нарушениях этого же акта (violatedMandatoryRequirements).
 */
export const removeMaterialsLinkedToVideoFixations = (
  formRef: ICommonFormRef | undefined,
  evidencesFieldName: string,
  videoFixationGuids: Array<string | null | undefined>,
): void => {
  const form = formRef?.current;

  if (!form || !evidencesFieldName.endsWith('.violationEvidences')) {
    return;
  }

  const requirementsPath = evidencesFieldName.replace(/\.violationEvidences$/, '.violatedMandatoryRequirements');
  const requirements = form.getFieldState(requirementsPath)?.value as
    | IKnmErknmFormViolatedRequirementsNpa[]
    | undefined;

  removeLinkedVideoFixationsFromRequirements(form, requirementsPath, requirements, videoFixationGuids);
};

const removeViolationEventsLinkedToPassportEvent = (
  form: FormApi,
  requirementsPath: string,
  requirements: IKnmErknmFormViolatedRequirementsNpa[] | undefined,
  eventGuid: string,
): void => {
  if (!requirements?.length) {
    return;
  }

  requirements.forEach((npa, npaIndex) => {
    npa.violatedMandatory?.forEach((mandatory, mandatoryIndex) => {
      mandatory.violations?.forEach((violation: IKnmErknmFormViolation, violationIndex) => {
        const events = violation.events as IKnmErknmFormViolationEvent[] | undefined;
        const violationPath = `${requirementsPath}[${npaIndex}].violatedMandatory[${mandatoryIndex}].violations[${violationIndex}]`;

        if (!events?.length) {
          return;
        }

        const filteredEvents = events.filter(event => event.eventOption?.value !== eventGuid);

        if (filteredEvents.length === events.length) {
          return;
        }

        if (!filteredEvents.length) {
          form.change(`${violationPath}.inEvent`, negativeSelectOption);
          form.change(`${violationPath}.events`, undefined);

          return;
        }

        form.change(`${violationPath}.events`, filteredEvents);
      });
    });
  });
};

const removeViolationEvidencesLinkedToPassportEvent = (
  form: FormApi,
  actPath: string,
  evidences: IKnmErknmViolationEvidence[] | undefined,
  requirements: IKnmErknmFormViolatedRequirementsNpa[] | undefined,
  eventGuid: string,
): void => {
  if (!evidences?.length) {
    return;
  }

  const evidencesPath = `${actPath}.violationEvidences`;
  const { remove } = form.mutators;

  const removedVideoFixationGuids: Array<string | null> = [];
  const indexesToRemove: number[] = [];

  evidences.forEach((evidence, index) => {
    if (evidence.eventOption?.value === eventGuid) {
      indexesToRemove.push(index);
      removedVideoFixationGuids.push(evidence.guid);
    }
  });

  if (!indexesToRemove.length) {
    return;
  }

  // FieldArray: удаляем с конца, чтобы не сбивать индексы
  if (remove) {
    for (let i = indexesToRemove.length - 1; i >= 0; i -= 1) {
      remove(evidencesPath, indexesToRemove[i]);
    }
  } else {
    const filteredEvidences = evidences.filter((_, index) => !indexesToRemove.includes(index));

    form.change(evidencesPath, filteredEvidences.length ? filteredEvidences : undefined);
  }

  if (evidences.length - indexesToRemove.length <= 0) {
    form.change(`${actPath}.isViolationEvidenceExists`, negativeSelectOption);
  }

  removeLinkedVideoFixationsFromRequirements(
    form,
    `${actPath}.violatedMandatoryRequirements`,
    requirements,
    removedVideoFixationGuids,
  );
};

/**
 * При удалении паспортного действия чистим связанные данные только в актах:
 * - violationEvidences с этим действием (ИР) + связанные materials в нарушениях
 * - events нарушений акта с этим действием
 */
export const removeLinkedDataByPassportEventGuid = (
  formRef: ICommonFormRef | undefined,
  eventGuid: string | null | undefined,
  isKnm: boolean,
  isPm: boolean,
  isDecision: boolean,
): void => {
  const form = formRef?.current;

  if (!form || !eventGuid) {
    return;
  }

  getActCleanupTargets(form, isKnm, isPm, isDecision).forEach(act => {
    removeViolationEvidencesLinkedToPassportEvent(form, act.actPath, act.evidences, act.requirements, eventGuid);

    removeViolationEventsLinkedToPassportEvent(
      form,
      `${act.actPath}.violatedMandatoryRequirements`,
      act.requirements,
      eventGuid,
    );
  });
};
